import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { CheckCircle, CheckCircle2, Filter, RefreshCw, ShieldCheck, XCircle } from 'lucide-react';

const API = axios.create({ baseURL: '/api/v1' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const VAT_INPUT_ACCOUNT = '1300-20-01';
const VAT_RATE = 0.15;

interface DebitEvent {
  id: number;
  classification: string;
  supplierId: string | null;
  targetAccount: string | null;
  targetAccountName?: string | null;
  bankAccount: string;
  bankAccountName?: string | null;
  amount: string;
  currency: string;
  valueDate: string | null;
  bankReference: string | null;
  narrative: string | null;
  postingMode: string;
  status: string;
  journalReference: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  suggestedVatAmount?: number;
  suggestedNetAmount?: number;
  vatExempt?: boolean;
  suggestSplitInputVat?: boolean;
}

interface Summary {
  pendingCount: number;
  pendingAmount: string;
  currency: string;
}

const STATUS_CLASS: Record<string, string> = {
  pending_review: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
  auto_posted: 'bg-blue-50 text-blue-700',
  recon_only: 'bg-gray-100 text-gray-600',
};

const LIMIT = 25;

function errorMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  return axiosError.response?.data?.message || axiosError.response?.data?.error || fallback;
}

function formatRand(value: string | number): string {
  return Number(value || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function inclusiveVat(gross: number): number {
  return Number((Number(gross) * VAT_RATE / (1 + VAT_RATE)).toFixed(2));
}

function accountLabel(
  code: string | null | undefined,
  name?: string | null,
  accounts?: Record<string, string>,
): string {
  if (!code) return 'unassigned';
  const resolved = name || accounts?.[code];
  return resolved ? `${code} — ${resolved}` : code;
}

export const TreasuryOutboundDebitEventsOverlay: React.FC = () => {
  const [events, setEvents] = useState<DebitEvent[]>([]);
  const [selected, setSelected] = useState<DebitEvent | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [ledgerAccounts, setLedgerAccounts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [splitInputVat, setSplitInputVat] = useState(false);
  const [targetOverride, setTargetOverride] = useState('');
  const [note, setNote] = useState('');

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / LIMIT)), [total]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get('/treasury/debit-events', {
        params: { page, limit: LIMIT, status: statusFilter },
      });
      setEvents(res.data.data.events);
      setTotal(res.data.data.pagination.total);
      setSummary(res.data.data.summary);
      setLedgerAccounts(res.data.data.ledgerAccounts || {});
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to load debit events'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const selectEvent = (event: DebitEvent) => {
    setSelected(event);
    setSplitInputVat(Boolean(event.suggestSplitInputVat) && !event.vatExempt);
    setTargetOverride(event.targetAccount || '');
    setNote('');
    setNotice(null);
    setError(null);
  };

  const isFloatTopup = selected?.postingMode === 'auto_float_topup';
  const overrideName = targetOverride.trim()
    ? ledgerAccounts[targetOverride.trim()] || null
    : null;

  const preview = useMemo(() => {
    if (!selected || isFloatTopup) return null;
    const gross = Number(selected.amount);
    const target = targetOverride.trim() || selected.targetAccount || '';
    const vat = selected.vatExempt || !splitInputVat
      ? 0
      : (typeof selected.suggestedVatAmount === 'number' && selected.suggestSplitInputVat
        ? Number(selected.suggestedVatAmount)
        : inclusiveVat(gross));
    const net = Number((gross - vat).toFixed(2));
    const lines: { dc: string; code: string; name?: string | null; amount: number }[] = [
      { dc: 'DR', code: target || 'unassigned', name: overrideName || selected.targetAccountName, amount: net },
    ];
    if (vat > 0) {
      lines.push({
        dc: 'DR',
        code: VAT_INPUT_ACCOUNT,
        name: ledgerAccounts[VAT_INPUT_ACCOUNT] || 'VAT Input Recoverable',
        amount: vat,
      });
    }
    lines.push({
      dc: 'CR',
      code: selected.bankAccount,
      name: selected.bankAccountName || ledgerAccounts[selected.bankAccount],
      amount: gross,
    });
    return { gross, vat, net, lines };
  }, [selected, isFloatTopup, splitInputVat, targetOverride, overrideName, ledgerAccounts]);

  const approve = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const body: Record<string, unknown> = { note: note || undefined };
      if (!isFloatTopup) {
        body.splitInputVat = Boolean(splitInputVat) && !selected.vatExempt;
        body.targetAccount = targetOverride || undefined;
      }
      await API.post(`/treasury/debit-events/${selected.id}/approve`, body);
      setNotice(`Debit event #${selected.id} approved and journalled.`);
      setSelected(null);
      await fetchEvents();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to approve debit event'));
    } finally {
      setSaving(false);
    }
  };

  const reject = async () => {
    if (!selected) return;
    const reason = window.prompt('Reason for rejection?');
    if (!reason) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await API.post(`/treasury/debit-events/${selected.id}/reject`, { reason });
      setNotice(`Debit event #${selected.id} rejected.`);
      setSelected(null);
      await fetchEvents();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to reject debit event'));
    } finally {
      setSaving(false);
    }
  };

  const confirmReconciled = async () => {
    if (!selected) return;
    const confirmNote = note || window.prompt('Confirm this line is already in the books? Add a short note.');
    if (!confirmNote) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await API.post(`/treasury/debit-events/${selected.id}/confirm-reconciled`, { note: confirmNote });
      setNotice(`Debit event #${selected.id} confirmed reconciled. No journal posted.`);
      setSelected(null);
      await fetchEvents();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to confirm debit event'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mymoolah-card flex flex-col gap-4 p-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-[var(--primary)]" />
            <h1 className="text-2xl font-semibold text-[var(--foreground)]">Treasury Outbound Debits</h1>
          </div>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Maker-checker review of classified bank-statement debits (float top-ups, bank charges, PayShap fees).
          </p>
          {summary && (
            <p className={`mt-2 text-sm font-medium ${Number(summary.pendingCount) === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
              {Number(summary.pendingCount) === 0
                ? 'Queue clear — every outbound debit is reconciled or confirmed'
                : `${summary.pendingCount} pending · R ${formatRand(summary.pendingAmount)} awaiting review`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
          >
            <option value="pending_review">Pending review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="auto_posted">Auto-posted</option>
            <option value="recon_only">Recon only</option>
            <option value="all">All</option>
          </select>
          <button
            type="button"
            onClick={fetchEvents}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {notice && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">{notice}</div>}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="mymoolah-card overflow-hidden">
          {loading && events.length === 0 ? (
            <div className="p-8 text-center text-gray-400">Loading debit events...</div>
          ) : events.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500">
              {statusFilter === 'pending_review' && summary && Number(summary.pendingCount) === 0 ? (
                <>
                  <CheckCircle className="mx-auto mb-2 h-6 w-6 text-emerald-500" />
                  <p className="font-medium text-emerald-700">Queue is clear</p>
                  <p className="mt-1">Every outbound debit is reconciled or confirmed.</p>
                </>
              ) : (
                <>
                  <Filter className="mx-auto mb-2 h-5 w-5 text-gray-300" />
                  No debit events match the current filter.
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-left">
                    {['Event', 'Classification', 'Supplier', 'Amount', 'Status', 'Value date'].map((header) => (
                      <th key={header} className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {events.map((event) => (
                    <tr key={event.id} onClick={() => selectEvent(event)} className="cursor-pointer hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-800">#{event.id}</div>
                        <div className="max-w-[160px] truncate text-xs text-gray-400">{event.bankReference || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{event.classification.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-gray-600">{event.supplierId || '—'}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">R {formatRand(event.amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[event.status] || STATUS_CLASS.recon_only}`}>
                          {event.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{event.valueDate || new Date(event.createdAt).toLocaleDateString('en-ZA')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-500">
            <span>Page {page} of {totalPages} ({total} events)</span>
            <div className="flex gap-2">
              <button className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
              <button className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          </div>
        </div>

        <aside className="mymoolah-card min-h-[520px] p-5">
          {!selected ? (
            <div className="flex h-full min-h-[400px] items-center justify-center text-center text-sm text-gray-500">
              Select a debit event to review and post its journal.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Event #{selected.id}</h2>
                  <p className="text-xs text-gray-500">{selected.classification.replace(/_/g, ' ')}</p>
                </div>
                <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setSelected(null)}>Close</button>
              </div>

              <div className="rounded-xl bg-gray-50 p-4 text-xs text-gray-600">
                <div className="space-y-1">
                  <p><span className="font-medium">Amount:</span> R {formatRand(selected.amount)} {selected.currency}</p>
                  <p><span className="font-medium">Supplier:</span> {selected.supplierId || '—'}</p>
                  <p><span className="font-medium">Target account:</span> {accountLabel(selected.targetAccount, selected.targetAccountName, ledgerAccounts)}</p>
                  <p><span className="font-medium">Bank account:</span> {accountLabel(selected.bankAccount, selected.bankAccountName, ledgerAccounts)}</p>
                  <p><span className="font-medium">Bank ref:</span> {selected.bankReference || '—'}</p>
                  <p><span className="font-medium">Value date:</span> {selected.valueDate || '—'}</p>
                  {selected.narrative && <p className="break-words"><span className="font-medium">Narrative:</span> {selected.narrative}</p>}
                </div>
              </div>

              {selected.status === 'pending_review' ? (
                <div className="space-y-3">
                  {!isFloatTopup && (
                    <>
                      <label className="block text-xs font-semibold text-gray-600">Target ledger account</label>
                      <input className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="e.g. 5100-01-01" value={targetOverride} onChange={(e) => setTargetOverride(e.target.value)} />
                      <p className="text-xs text-gray-500">
                        {targetOverride.trim()
                          ? (overrideName ? overrideName : 'Unknown ledger code')
                          : 'Enter a ledger code to allocate this debit'}
                      </p>
                      {selected.vatExempt ? (
                        <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                          Overdraft interest is VAT-exempt. The full amount posts to the target expense account with no input-VAT split.
                        </p>
                      ) : (
                        <label className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={splitInputVat}
                            onChange={(e) => setSplitInputVat(e.target.checked)}
                          />
                          <span>
                            Split 15% input VAT (statement amount is VAT-inclusive; monthly SBSA statement is the tax invoice). Untick only if there is no tax invoice.
                          </span>
                        </label>
                      )}
                      {preview && (
                        <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700">
                          <p className="mb-2 font-semibold text-gray-800">Will post</p>
                          <ul className="space-y-1 font-mono">
                            {preview.lines.map((line) => (
                              <li key={`${line.dc}-${line.code}`} className="flex justify-between gap-2">
                                <span>{line.dc} {accountLabel(line.code, line.name, ledgerAccounts)}</span>
                                <span>R {formatRand(line.amount)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                  {isFloatTopup && (
                    <p className="rounded-lg bg-blue-50 p-3 text-xs text-blue-700">
                      Float top-up: will post DR {accountLabel(selected.targetAccount, selected.targetAccountName, ledgerAccounts)} / CR {accountLabel(selected.bankAccount, selected.bankAccountName, ledgerAccounts)} for the full amount and sync the supplier float mirror.
                    </p>
                  )}
                  <label className="block text-xs font-semibold text-gray-600">Review note (optional)</label>
                  <textarea className="h-16 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" value={note} onChange={(e) => setNote(e.target.value)} />
                  <div className="flex flex-wrap gap-2">
                    <button disabled={saving} onClick={approve} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-50"><CheckCircle className="h-4 w-4" />Approve &amp; post</button>
                    <button disabled={saving} onClick={confirmReconciled} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 disabled:opacity-50"><CheckCircle2 className="h-4 w-4" />Confirm reconciled</button>
                    <button disabled={saving} onClick={reject} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 disabled:opacity-50"><XCircle className="h-4 w-4" />Reject</button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-gray-100 p-4 text-xs text-gray-600">
                  <p><span className="font-medium">Status:</span> {selected.status.replace(/_/g, ' ')}</p>
                  {selected.journalReference && <p><span className="font-medium">Journal:</span> {selected.journalReference}</p>}
                  {selected.reviewedBy && <p><span className="font-medium">Reviewed by:</span> {selected.reviewedBy}</p>}
                  {selected.reviewedAt && <p><span className="font-medium">Reviewed at:</span> {new Date(selected.reviewedAt).toLocaleString('en-ZA')}</p>}
                  {selected.reviewNote && <p className="mt-1 text-gray-500">{selected.reviewNote}</p>}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};
